/**
 * @fileoverview Pre-Join Dumps tab, "Live now" view: visitors on the instant-demo
 * setup form right now (GET /api/tracking/prejoin-live), each with a Watch live
 * button into the same {@link LiveScreenViewerComponent} the Live table uses.
 */

import { Component, OnDestroy, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TrackingApiService } from '../services/tracking-api.service';
import { LiveScreenViewerComponent, type LiveScreenTarget } from './live-screen-viewer.component';
import { parseApiDate } from '../tracking-format.util';
import { visitorDevice, visitorPlace } from '../prejoin-visitor.util';
import type { PrejoinLiveRow } from '../prejoin-live.models';

/** How often the list refreshes (visitors come and go by the second). */
const POLL_MS = 5000;

/** Live list of setup-form visitors, polled while the view is open. */
@Component({
  selector: 'app-prejoin-live',
  standalone: true,
  imports: [CommonModule, LiveScreenViewerComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="overflow-x-auto rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1">
      <table class="min-w-full text-sm">
        <thead class="bg-slate-50 dark:bg-white/5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-ink-soft/50">
          <tr>
            <th class="px-3 py-2 text-left font-medium">Started</th>
            <th class="px-3 py-2 text-left font-medium">Session</th>
            <th class="px-3 py-2 text-left font-medium">Product</th>
            <th class="px-3 py-2 text-left font-medium">Location · IP</th>
            <th class="px-3 py-2 text-left font-medium">Device</th>
            <th class="px-3 py-2 text-right font-medium">Time on form</th>
            <th class="px-3 py-2 text-right font-medium">Clicks</th>
            <th class="px-3 py-2 text-right font-medium">Rage</th>
            <th class="px-3 py-2 text-right font-medium">Dead</th>
            <th class="px-3 py-2 text-right font-medium"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-line/60">
          @for (r of rows(); track r.sessionId) {
            <tr class="hover:bg-slate-50 dark:hover:bg-line/40">
              <td class="px-3 py-2 tabular-nums text-gray-500">{{ timeOf(r.startedAt) }}</td>
              <td class="px-3 py-2 font-mono text-[11px] text-gray-500">{{ r.sessionId }}</td>
              <td class="px-3 py-2 capitalize text-gray-600 dark:text-gray-300">{{ r.productKeyname || '—' }}</td>
              <td class="px-3 py-2">
                <div class="text-gray-700 dark:text-gray-200">{{ visitorPlace(r.visitor) }}</div>
                @if (r.visitor?.ip) {
                  <div class="font-mono text-[11px] text-gray-500" [title]="r.visitor?.isp || ''">{{ r.visitor?.ip }}</div>
                }
              </td>
              <td class="px-3 py-2 text-gray-600 dark:text-gray-300">{{ visitorDevice(r.visitor) }}</td>
              <td class="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-200">{{ elapsed(r.startedAt) }}</td>
              <td class="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-200">{{ r.clickCount }}</td>
              <td class="px-3 py-2 text-right tabular-nums" [class]="r.rageClicks > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'">{{ r.rageClicks }}</td>
              <td class="px-3 py-2 text-right tabular-nums" [class]="r.deadClicks > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500'">{{ r.deadClicks }}</td>
              <td class="px-3 py-2 text-right">
                <button type="button" (click)="watch(r)"
                  class="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10">
                  <span class="h-1.5 w-1.5 rounded-full bg-rose-500"></span> Watch live
                </button>
              </td>
            </tr>
          } @empty {
            <tr><td colspan="10" class="px-3 py-10 text-center text-gray-400">
              {{ loading() ? 'Loading…' : 'Nobody is on the setup form right now.' }}
            </td></tr>
          }
        </tbody>
      </table>
    </div>
    <div class="mt-2 text-xs text-gray-400">
      {{ rows().length }} visitor(s) on the form · refreshes every {{ pollSeconds }}s ·
      a visitor leaves this list once they join the call or close the form.
    </div>

    @if (watching(); as target) {
      @defer {
        <app-live-screen-viewer [session]="target" (closed)="watching.set(null)"></app-live-screen-viewer>
      }
    }
  `,
})
export class PrejoinLiveComponent implements OnInit, OnDestroy {
  private readonly api = inject(TrackingApiService);

  protected readonly rows = signal<PrejoinLiveRow[]>([]);
  protected readonly loading = signal(true);
  protected readonly now = signal(Date.now());
  protected readonly watching = signal<LiveScreenTarget | null>(null);
  protected readonly pollSeconds = POLL_MS / 1000;
  protected readonly visitorPlace = visitorPlace;
  protected readonly visitorDevice = visitorDevice;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private clockTimer: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    void this.load();
    this.pollTimer = setInterval(() => void this.load(), POLL_MS);
    this.clockTimer = setInterval(() => this.now.set(Date.now()), 1000);
  }

  ngOnDestroy(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
    }
    if (this.clockTimer !== null) {
      clearInterval(this.clockTimer);
    }
  }

  protected watch(r: PrejoinLiveRow): void {
    this.watching.set({ sessionId: r.sessionId, hostName: 'Visitor on the setup form', companyName: null });
  }

  protected timeOf(iso: string | null): string {
    const d = parseApiDate(iso);
    return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
  }

  /** Ticking "time on form" from the capture start. */
  protected elapsed(iso: string | null): string {
    const d = parseApiDate(iso);
    if (!d) {
      return '—';
    }
    const s = Math.max(0, Math.round((this.now() - d.getTime()) / 1000));
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  }

  private async load(): Promise<void> {
    try {
      const res = await this.api.getPrejoinLive();
      this.rows.set(res.items || []);
    } catch {
      // The api service reports errors; keep showing the last list.
    } finally {
      this.loading.set(false);
    }
  }
}
